import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { MapPin, Home, CalendarCheck } from "lucide-react";

const EASE = [0.22, 1, 0.36, 1];

const MaskLine = ({ children, delay, className = "" }) => (
    <span className={`block overflow-hidden ${className}`}>
        <motion.span
            className="block"
            initial={{ y: "112%" }}
            animate={{ y: 0 }}
            transition={{ duration: 1, delay, ease: EASE }}
        >
            {children}
        </motion.span>
    </span>
);

const Hero = () => {
    const { scrollY } = useScroll();
    const yPalms = useTransform(scrollY, [0, 700], [0, 90]);
    const yGlow = useTransform(scrollY, [0, 700], [0, -50]);

    return (
        <section
            id="accueil"
            data-testid="hero-section"
            className="relative flex min-h-[100svh] items-center overflow-hidden"
        >
            {/* Palmiers nocturnes — parallaxe légère */}
            <motion.div style={{ y: yPalms }} className="pointer-events-none absolute inset-0">
                <img
                    src="https://images.unsplash.com/photo-1545653026-b4e1659b3731?crop=entropy&cs=srgb&fm=jpg&q=85"
                    alt=""
                    aria-hidden="true"
                    className="mask-fade-b h-full w-full object-cover opacity-[0.16]"
                    loading="eager"
                />
            </motion.div>
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-noir/60 via-transparent to-noir" />

            {/* Halos cyan */}
            <motion.div style={{ y: yGlow }} className="pointer-events-none absolute inset-0">
                <div className="halo absolute -top-32 left-1/2 h-[34rem] w-[34rem] -translate-x-1/2 rounded-full" />
                <div className="halo absolute bottom-0 right-[-10rem] h-[26rem] w-[26rem] rounded-full opacity-70" />
            </motion.div>

            <div className="relative z-10 mx-auto w-full max-w-6xl px-4 pb-24 pt-28 sm:px-6 sm:pt-32">
                <motion.img
                    src="/brand/logo.webp"
                    alt="Clean My Life"
                    data-testid="hero-logo"
                    initial={{ opacity: 0, scale: 0.94, filter: "blur(8px)" }}
                    animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
                    transition={{ duration: 1.1, ease: EASE }}
                    className="w-40 sm:w-52"
                />

                <motion.div
                    initial={{ opacity: 0, y: 14 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.5, ease: EASE }}
                    className="mt-8 inline-flex items-center gap-2 rounded-full border border-neon/30 bg-neon/5 px-4 py-1.5"
                    data-testid="hero-location-badge"
                >
                    <MapPin size={14} className="text-neon" />
                    <span className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-neon">
                        Cannes & alentours
                    </span>
                </motion.div>

                <h1 className="mt-6 font-display text-4xl font-extrabold leading-[1.06] tracking-tight text-white sm:text-5xl lg:text-6xl">
                    <MaskLine delay={0.65}>Nettoyage automobile</MaskLine>
                    <MaskLine delay={0.8}>
                        <span className="text-glow text-neon">à domicile</span>
                    </MaskLine>
                </h1>

                <motion.p
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 1.1, ease: EASE }}
                    className="mt-6 font-display text-xl font-bold italic tracking-wide text-shine sm:text-2xl"
                    data-testid="hero-slogan"
                >
                    « On nettoie, vous brillez. »
                </motion.p>

                <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.8, delay: 1.25 }}
                    className="mt-4 max-w-xl text-base leading-relaxed text-slate-400 sm:text-lg"
                >
                    Detailing et lavage automobile premium, directement chez vous sur la
                    Côte d'Azur. Vous réservez, nous nous déplaçons.
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 18 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 1.45, ease: EASE }}
                    className="mt-10 flex flex-col gap-3 sm:flex-row sm:items-center"
                >
                    <Link
                        to="/reservation"
                        data-testid="hero-reserve-button"
                        className="rounded-full bg-neon px-8 py-4 text-center text-base font-bold tracking-wide text-noir transition-[box-shadow,transform] duration-300 hover:-translate-y-0.5 hover:shadow-[0_0_40px_rgba(0,240,255,0.5)]"
                    >
                        RÉSERVER EN LIGNE
                    </Link>
                    <a
                        href="#formules"
                        data-testid="hero-discover-button"
                        className="rounded-full border border-white/15 px-8 py-4 text-center text-base font-semibold text-slate-200 transition-colors duration-300 hover:border-neon/50 hover:text-neon"
                    >
                        DÉCOUVRIR LES FORMULES
                    </a>
                </motion.div>

                <motion.ul
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.9, delay: 1.7 }}
                    className="mt-12 flex flex-wrap gap-x-8 gap-y-3 text-sm text-slate-400"
                    data-testid="hero-quick-points"
                >
                    <li className="flex items-center gap-2">
                        <Home size={15} className="text-neon" /> Service chez vous
                    </li>
                    <li className="flex items-center gap-2">
                        <MapPin size={15} className="text-neon" /> Cannes & alentours
                    </li>
                    <li className="flex items-center gap-2">
                        <CalendarCheck size={15} className="text-neon" /> Réservation en ligne
                    </li>
                </motion.ul>
            </div>
        </section>
    );
};

export default Hero;
